import { FLOW_CONTROL_SOCKET_TYPE } from '@erp/workflows';

/**
 * Socket-type compatibility for workflow edges (output → input).
 *
 * Re-exported from `@erp/workflows` rather than reimplemented. This file used
 * to carry its own copy, which silently drifted: the shared version learned to
 * accept `array<string> → number` (a CDC observe step types every ingest
 * column `array<string>` because the foreign table is all TEXT), the copy here
 * did not, and the editor rejected edges the backend accepts as valid — an
 * imported CDC workflow could not be saved.
 *
 * Only the presentation helpers below are frontend-specific; the predicate
 * itself must stay single-sourced.
 */
export { areSocketTypesCompatible } from '@erp/workflows';

import { areSocketTypesCompatible } from '@erp/workflows';

function humanizeSocketType(t: string): string {
  if (t === FLOW_CONTROL_SOCKET_TYPE) {
    return 'a flow-order connector';
  }
  if (t === 'any') {
    return 'any type';
  }
  if (t === 'uuid') {
    return 'a UUID';
  }
  if (t === 'string') {
    return 'text';
  }
  if (t === 'number') {
    return 'a number';
  }
  if (t === 'boolean') {
    return 'true/false';
  }
  if (t === 'json') {
    return 'structured JSON';
  }
  if (t === 'date') {
    return 'a date';
  }
  if (t === 'signal') {
    return 'a signal';
  }
  if (t.startsWith('array<') && t.endsWith('>')) {
    const inner = t.slice(6, -1);
    return `a list of ${humanizeSocketType(inner)}`;
  }
  if (t.startsWith('entity_reference:')) {
    const label = t
      .slice('entity_reference:'.length)
      .replace(/_/g, ' ')
      .toLowerCase();
    return `a ${label} reference`;
  }
  if (t.startsWith('entity_reference')) {
    return 'an entity reference';
  }
  return t;
}

/**
 * When an output type cannot wire into an input type, returns a short sentence for UI (toast/tooltip).
 */
export function explainSocketTypeIncompatibility(
  outputType: string,
  inputType: string,
): string | null {
  if (areSocketTypesCompatible(outputType, inputType)) {
    return null;
  }

  if (outputType === FLOW_CONTROL_SOCKET_TYPE) {
    return '“After complete” only connects to “Wait for” on another step—not to a data input.';
  }

  const out = humanizeSocketType(outputType);
  const inp = humanizeSocketType(inputType);
  return `That output is ${out}, but this input expects ${inp}.`;
}
